import { useEffect, useRef, useState } from 'react';

import { skipToken } from '@reduxjs/toolkit/query';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
  CreateSchedulerEventInput,
  useCreateSchedulerEventMutation,
  useDeleteSchedulerEventMutation,
  useGetSchedulerEventByIdQuery,
  useSchedulerReferenceUserOptions,
  useUpdateSchedulerEventMutation,
} from '@/app/services/serviceScheduler';
import {
  closeModal,
  EventFormValues,
  openForEdit,
} from '@/app/slices/createEventModalSlice';
import { addToast } from '@/app/slices/toast';
import type { RootState } from '@/app/store';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import {
  Button,
  Calendar,
  Chips,
  Dropdown,
  InputText,
  InputTextarea,
} from '../compat-controls';

const GUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const isGuid = (value?: string) => !!value && GUID_REGEX.test(value);

function RequiredLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="mb-1 block text-sm font-medium text-gray-700">
      {children} <span className="text-red-500">*</span>
    </label>
  );
}

function OptionalLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="mb-1 block text-sm font-medium text-gray-700">
      {children}
    </label>
  );
}

export function CreateEventModal() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { open, mode, initialValues } = useSelector(
    (state: RootState) => state.createEventModal,
  );

  const EVENT_TYPES = [
    { label: t('serviceScheduler.collection'), value: 'collection' },
    { label: t('transfer.transfer'), value: 'transfer' },
  ];
  const { options: vetOptions } = useSchedulerReferenceUserOptions({
    role: 'psv',
    activeOnly: true,
    startRow: 1,
    endRow: 200,
  });
  const { options: pseOptions } = useSchedulerReferenceUserOptions({
    role: 'pse',
    activeOnly: true,
    startRow: 1,
    endRow: 200,
  });
  const [createEvent, { isLoading: isCreating }] =
    useCreateSchedulerEventMutation();
  const [updateEvent, { isLoading: isUpdating }] =
    useUpdateSchedulerEventMutation();
  const [deleteEvent, { isLoading: isDeleting }] =
    useDeleteSchedulerEventMutation();

  type LocalFormValues = Omit<EventFormValues, 'startDate' | 'endDate'> & {
    startDate: Date | null;
    endDate: Date | null;
  };
  const [form, setForm] = useState<LocalFormValues>({
    type: null,
    psVet: null,
    startDate: null,
    endDate: null,
    coveringSite: '',
    description: '',
    enteredBy: '',
    psEmbryologist: null,
    attendees: [],
  });
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const eventIdForQuery =
    mode === 'edit' && open && isGuid(initialValues?.id)
      ? initialValues.id
      : skipToken;
  const { data: eventDetail, isFetching: isFetchingDetail } =
    useGetSchedulerEventByIdQuery(eventIdForQuery);
  const hydratedEventIdRef = useRef<string | null>(null);
  const isSaving = isCreating || isUpdating;
  const isBusy = isSaving || isDeleting || isFetchingDetail;

  useEffect(() => {
    if (open && initialValues) {
      setForm({
        ...initialValues,
        startDate: initialValues.startDate
          ? new Date(initialValues.startDate)
          : null,
        endDate: initialValues.endDate ? new Date(initialValues.endDate) : null,
      });
    }
  }, [open, initialValues]);

  useEffect(() => {
    if (!open) {
      hydratedEventIdRef.current = null;
      return;
    }
    if (mode !== 'edit' || !eventDetail) return;
    if (hydratedEventIdRef.current === eventDetail.eventId) return;

    const mapped: EventFormValues = {
      id: eventDetail.eventId,
      type: eventDetail.type ?? null,
      psVet: eventDetail.psVetId ?? null,
      startDate: eventDetail.startUtc ?? null,
      endDate: eventDetail.endUtc ?? null,
      coveringSite: eventDetail.coveringSiteCode ?? '',
      description: eventDetail.description ?? '',
      enteredBy: eventDetail.enteredBy ?? '',
      psEmbryologist: eventDetail.psEmbryologistId ?? null,
      attendees: eventDetail.attendees ?? [],
    };
    dispatch(openForEdit(mapped));
    hydratedEventIdRef.current = eventDetail.eventId;
  }, [dispatch, eventDetail, mode, open]);

  const hide = () => dispatch(closeModal());

  const mapEventInput = (): CreateSchedulerEventInput => ({
    type: form.type ?? '',
    psVetId: form.psVet,
    startUtc: form.startDate?.toISOString() ?? null,
    endUtc: form.endDate?.toISOString() ?? null,
    coveringSiteCode: form.coveringSite || null,
    description: form.description,
    enteredBy: form.enteredBy,
    psEmbryologistId: form.psEmbryologist,
    attendees: form.attendees ?? [],
  });

  const missingRequiredFields: string[] = [];
  if (!form.type) missingRequiredFields.push(t('common.type'));
  if (!form.startDate) missingRequiredFields.push(t('common.startDate'));
  if (!form.endDate) missingRequiredFields.push(t('common.endDate'));
  const hasInvalidDateRange =
    !!form.startDate && !!form.endDate && form.startDate > form.endDate;
  const isFormValid =
    missingRequiredFields.length === 0 && !hasInvalidDateRange;

  const handleSave = async () => {
    if (!isFormValid) {
      const detail = hasInvalidDateRange
        ? `${t('common.startDate')} > ${t('common.endDate')}`
        : `${t('common.missingFields')}: ${missingRequiredFields.join(', ')}`;
      dispatch(
        addToast({
          severity: 'error',
          summary: t('common.validationError'),
          detail,
          sticky: true,
        }),
      );
      return;
    }

    try {
      const input = mapEventInput();
      if (mode === 'edit' && form.id) {
        await updateEvent({ eventId: form.id, input }).unwrap();
      } else {
        await createEvent(input).unwrap();
      }

      dispatch(
        addToast({
          severity: 'success',
          summary: t('common.save'),
          detail:
            mode === 'edit'
              ? t('serviceScheduler.editEvent')
              : t('serviceScheduler.createEvent'),
        }),
      );
      hide();
    } catch (error: unknown) {
      const errObj = error as { data?: unknown } | undefined;
      const detail =
        typeof errObj?.data === 'string' ? errObj.data : t('common.error');
      dispatch(
        addToast({
          severity: 'error',
          summary: t('common.error'),
          detail,
          sticky: true,
        }),
      );
    }
  };

  const handleDelete = async () => {
    if (!form?.id || mode !== 'edit') return;
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!form?.id || mode !== 'edit') return;
    try {
      await deleteEvent({ eventId: form.id }).unwrap();
      dispatch(
        addToast({
          severity: 'success',
          summary: t('common.delete'),
          detail: t('serviceScheduler.editEvent'),
        }),
      );
      hide();
    } catch (error: unknown) {
      const errObj = error as { data?: unknown } | undefined;
      const detail =
        typeof errObj?.data === 'string' ? errObj.data : t('common.error');
      dispatch(
        addToast({
          severity: 'error',
          summary: t('common.error'),
          detail,
          sticky: true,
        }),
      );
    } finally {
      setConfirmDeleteOpen(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) hide();
        }}
      >
        <DialogContent className="md:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {mode === 'create'
                ? t('serviceScheduler.createEvent')
                : t('serviceScheduler.editEvent')}
            </DialogTitle>
          </DialogHeader>

          <DialogBody className="flex flex-col gap-4">
            <div>
              <RequiredLabel>{t('common.type')}</RequiredLabel>
              <Dropdown
                placeholder={t('common.type')}
                value={form.type}
                options={EVENT_TYPES}
                onChange={(e) => setForm((f) => ({ ...f, type: e.value }))}
                className="w-full"
              />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <RequiredLabel>{t('common.startDate')}</RequiredLabel>
                <Calendar
                  placeholder={t('common.startDate')}
                  value={form.startDate}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, startDate: e.value as Date }))
                  }
                  className="w-full"
                  showIcon
                  dateFormat="mm/dd/yy"
                />
              </div>
              <div>
                <RequiredLabel>{t('common.endDate')}</RequiredLabel>
                <Calendar
                  placeholder={t('common.endDate')}
                  value={form.endDate}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, endDate: e.value as Date }))
                  }
                  className="w-full"
                  showIcon
                  dateFormat="mm/dd/yy"
                />
              </div>
            </div>

            <div>
              <OptionalLabel>{t('serviceScheduler.psVet')}</OptionalLabel>
              <div className="flex gap-2">
                <Dropdown
                  placeholder={t('serviceScheduler.psVet')}
                  value={form.psVet}
                  options={vetOptions}
                  onChange={(e) => setForm((f) => ({ ...f, psVet: e.value }))}
                  className="w-full"
                />
                <Button
                  type="button"
                  label={t('common.clear')}
                  severity="secondary"
                  onClick={() => setForm((f) => ({ ...f, psVet: null }))}
                  disabled={!form.psVet || isBusy}
                />
              </div>
            </div>

            <div>
              <OptionalLabel>
                {t('serviceScheduler.coveringSite')}
              </OptionalLabel>
              <InputText
                placeholder={t('serviceScheduler.coveringSite')}
                value={form.coveringSite}
                onChange={(e) =>
                  setForm((f) => ({ ...f, coveringSite: e.target.value }))
                }
                className="w-full"
              />
            </div>

            <div>
              <OptionalLabel>
                {t('serviceScheduler.psEmbryologist')}
              </OptionalLabel>
              <div className="flex gap-2">
                <Dropdown
                  placeholder={t('serviceScheduler.psEmbryologist')}
                  value={form.psEmbryologist}
                  options={pseOptions}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, psEmbryologist: e.value }))
                  }
                  className="w-full"
                />
                <Button
                  type="button"
                  label={t('common.clear')}
                  severity="secondary"
                  onClick={() =>
                    setForm((f) => ({ ...f, psEmbryologist: null }))
                  }
                  disabled={!form.psEmbryologist || isBusy}
                />
              </div>
            </div>

            <div>
              <OptionalLabel>{t('common.description')}</OptionalLabel>
              <InputTextarea
                placeholder={t('common.description')}
                value={form.description}
                onChange={(e) =>
                  setForm((f) => ({ ...f, description: e.target.value }))
                }
                className="w-full"
                rows={4}
              />
            </div>

            <div>
              <OptionalLabel>{t('serviceScheduler.enteredBy')}</OptionalLabel>
              <InputText
                placeholder={t('serviceScheduler.enteredBy')}
                value={form.enteredBy}
                onChange={(e) =>
                  setForm((f) => ({ ...f, enteredBy: e.target.value }))
                }
                className="w-full"
              />
            </div>

            <div>
              <OptionalLabel>
                {t('serviceScheduler.addAttendees')}
              </OptionalLabel>
              <Chips
                value={form.attendees}
                onChange={(e) =>
                  setForm((f) => ({ ...f, attendees: e.value as string[] }))
                }
                placeholder={t('serviceScheduler.addAttendees')}
                separator=","
                allowDuplicate={false}
                className="w-full"
              />
            </div>
          </DialogBody>

          <DialogFooter className="sm:justify-between">
            {mode === 'edit' ? (
              <Button
                label={t('common.delete')}
                severity="danger"
                onClick={handleDelete}
                loading={isDeleting}
                disabled={isBusy}
              />
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button
                label={t('common.cancel')}
                severity="secondary"
                onClick={hide}
                disabled={isBusy}
              />
              <Button
                label={t('common.save')}
                icon="pi pi-check"
                onClick={handleSave}
                loading={isSaving}
                disabled={isBusy || !isFormValid}
              />
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <DialogContent className="md:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('common.delete')}</DialogTitle>
            <DialogDescription>{t('common.areYouSure')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              label={t('common.cancel')}
              severity="secondary"
              onClick={() => setConfirmDeleteOpen(false)}
              disabled={isDeleting}
            />
            <Button
              label={t('common.delete')}
              severity="danger"
              onClick={handleConfirmDelete}
              loading={isDeleting}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
